import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Order, TablesInsert, TablesUpdate } from '@t90/common/supabase';
import { useSupabase, queryKeys } from '../provider';

// =====================================================
// ORDER QUERIES
// =====================================================

interface OrderFilters {
  customerId?: string;
  status?: string;
  locationId?: string;
  priority?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
}

export function useOrders(filters?: OrderFilters) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.orders.list(filters),
    queryFn: async () => {
      let query = supabase
        .from('orders')
        .select(`
          *,
          customer:customers(id, customer_name),
          location:locations(id, location_name, location_code)
        `)
        .order('created_at', { ascending: false });

      if (filters?.customerId && filters.customerId.trim() !== '') {
        query = query.eq('customer_id', filters.customerId);
      }
      if (filters?.status && filters.status.trim() !== '') {
        query = query.eq('order_status', filters.status);
      }
      if (filters?.locationId && filters.locationId.trim() !== '') {
        query = query.eq('location_id', filters.locationId);
      }
      if (filters?.priority && filters.priority.trim() !== '') {
        query = query.eq('priority', filters.priority);
      }
      if (filters?.fromDate && filters.fromDate.trim() !== '') {
        query = query.gte('order_date', filters.fromDate);
      }
      if (filters?.toDate && filters.toDate.trim() !== '') {
        query = query.lte('order_date', filters.toDate);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}

export function useOrder(orderId: string | undefined) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.orders.detail(orderId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          customer:customers(id, customer_name, contact_email),
          location:locations(id, location_name, location_code),
          created_by_user:app_users(id, full_name),
          projects(
            id, project_number, project_status, quantity, units_completed,
            service_item:service_items(id, service_item_name, service_type:service_types(id, service_type_name))
          )
        `)
        .eq('id', orderId!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!orderId && orderId !== 'new',
  });
}

// =====================================================
// ORDER MUTATIONS
// =====================================================

type OrderInsert = TablesInsert<'orders'>;
type OrderUpdate = TablesUpdate<'orders'>;

export function useCreateOrder() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (order: OrderInsert) => {
      const { data, error } = await supabase
        .from('orders')
        .insert(order as never)
        .select()
        .single();

      if (error) throw error;
      return data as Order;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      toast.success(`Order ${data.order_number} created successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to create order: ${error.message}`);
    },
  });
}

export function useUpdateOrder() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: OrderUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('orders')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Order;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(data.id) });
      toast.success(`Order ${data.order_number} updated successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to update order: ${error.message}`);
    },
  });
}

export function useUpdateOrderStatus() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: Order['order_status'];
    }) => {
      const { data, error } = await supabase
        .from('orders')
        .update({ order_status: status } as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Order;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(data.id) });
      toast.success(`Order status updated to ${data.order_status}`);
    },
    onError: (error) => {
      toast.error(`Failed to update order status: ${error.message}`);
    },
  });
}

export function useDeleteOrder() {
  const supabase = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase.from('orders').delete().eq('id', orderId);

      if (error) throw error;
      return orderId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      toast.success('Order deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete order: ${error.message}`);
    },
  });
}
